import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { HttpErrorResponse } from '@angular/common/http';
import {
  OmniSearchResult,
  type OmniSearchHistoryAttachment,
} from '@brighthub/search';
import { BrightNavOmniSearchProvider } from '@brighthub/brightnav-search';
import {
  ClientSessionService,
  UiSession,
} from '@brighthub/uisession';
import { GenericResponseCode } from '@brighthub/api';
import { AmplitudeService } from '@brighthub/logging';
import { ClientSearchService } from '../client-search.service';
import { ClientSearchResultItem } from '../client-search-api.model';
import { ClientPrototypeSearchResultComponent } from '../client-prototype-search-result/client-prototype-search-result.component';
import { clientSearchAmplitudeProps } from '../client-search-analytics.util';
import { prepareClientSearch } from '../client-search-query.util';
import { ClientSearchOmniHistoryService } from './client-search-omni-history.service';

/**
 * Omni-search provider for client lookup by name or GUID.
 * Prefers matching {@link UiSession} tabs when the query overlaps open conversations, then uses the client search API.
 */
@Injectable({
  providedIn: 'root',
})
export class ClientSearchProvider extends BrightNavOmniSearchProvider<ClientSearchResultItem> {
  id = 'client';
  label = 'Search for Client';
  override description = 'Search for a client by name or GUID.';
  override shortcut = '@client';
  rolesAllowed = [];

  private readonly _clientService = inject(ClientSessionService);
  private readonly _clientSearchService = inject(ClientSearchService);
  private readonly _amplitude = inject(AmplitudeService);
  private readonly _toast = inject(MessageService);
  private readonly _searchHistoryStore = inject(ClientSearchOmniHistoryService);

  /** Recent client searches and persistence hooks for the omni UI. */
  readonly searchHistory: OmniSearchHistoryAttachment<ClientSearchResultItem> =
    {
      entries: this._searchHistoryStore.entries,
      recordSearch: (q, r) => this._searchHistoryStore.recordSearch(q, r),
      removeEntry: (id) => this._searchHistoryStore.removeEntry(id),
    };

  constructor() {
    super();
  }

  /**
   * Entry point for the client omni provider: classifies the query with {@link prepareClientSearch},
   * then runs GUID search, name search, or returns no results.
   */
  search(query: string): Observable<OmniSearchResult<ClientSearchResultItem>[]> {
    const raw = query.trim();
    const prep = prepareClientSearch(raw);

    if (prep.kind === 'skip') {
      return of([]);
    }

    if (prep.searchType === 'guid') {
      return this._searchGuidLocalThenApi(raw, prep.qForApi);
    }

    return this._searchNameMergeLocalAndRemote(raw);
  }

  /**
   * GUID-shaped query: if a local session matches (at most one by GUID), returns it immediately;
   * otherwise searches the API using the original query string.
   *
   * @param rawQuery Query as entered (sent to the remote search).
   * @param qForApi Normalized GUID substring used to match open {@link UiSession} tabs by `subjectData.guid`.
   */
  private _searchGuidLocalThenApi(
    rawQuery: string,
    qForApi: string
  ): Observable<OmniSearchResult<ClientSearchResultItem>[]> {
    const qNorm = qForApi.toLowerCase();
    const localSession = this._clientService.sessions().find((s) =>
      s.subjectData.guid.toLowerCase().includes(qNorm)
    );
    const localMatches: UiSession[] = localSession ? [localSession] : [];

    if (localMatches.length > 0) {
      this._amplitude.logEvent('client_search_local_match', {
        ...clientSearchAmplitudeProps(rawQuery),
        count: localMatches.length,
      });
      return of(this._sessionsToOmniResults(localMatches));
    }

    return this._remoteSearchAsOmni(rawQuery);
  }

  /**
   * Name query: loads remote matches, merges in local open-session hits, and dedupes by GUID (local wins).
   */
  private _searchNameMergeLocalAndRemote(
    rawQuery: string
  ): Observable<OmniSearchResult<ClientSearchResultItem>[]> {
    const q = rawQuery.trim().toLowerCase();
    const localMatches = this._clientService.sessions().filter((s) =>
      `${s.subjectData.firstName} ${s.subjectData.lastName}`
        .toLowerCase()
        .includes(q)
    );
    const localOmni = this._sessionsToOmniResults(localMatches);

    return this._remoteSearchAsOmni(rawQuery).pipe(
      map((remoteOmni) =>
        this._mergeDedupeByGuidPreferLocal(localOmni, remoteOmni)
      )
    );
  }

  /** Converts UI sessions into omni rows whose `data` is {@link ClientSearchResultItem} with `sourceUiSession` set. */
  private _sessionsToOmniResults(
    sessions: UiSession[]
  ): OmniSearchResult<ClientSearchResultItem>[] {
    return sessions.map((s) => ({
      title: `${s.subjectData.firstName} ${s.subjectData.lastName}`,
      description: s.subjectData.guid,
      data: { ...this._sessionToSearchItem(s), sourceUiSession: s },
    }));
  }

  /**
   * Union of local and remote omni rows keyed by normalized GUID; the first (local) row wins on collision.
   * Rows with no usable `data.guid` are dropped.
   */
  private _mergeDedupeByGuidPreferLocal(
    local: OmniSearchResult<ClientSearchResultItem>[],
    remote: OmniSearchResult<ClientSearchResultItem>[]
  ): OmniSearchResult<ClientSearchResultItem>[] {
    const byGuid = new Map<string, OmniSearchResult<ClientSearchResultItem>>();

    const guidKey = (
      item: OmniSearchResult<ClientSearchResultItem>
    ): string | null => {
      const g = item.data.guid?.trim().toLowerCase();
      return g || null;
    };

    for (const item of local) {
      const k = guidKey(item);
      if (k) {
        byGuid.set(k, item);
      }
    }
    for (const item of remote) {
      const k = guidKey(item);
      if (k && !byGuid.has(k)) {
        byGuid.set(k, item);
      }
    }

    return [...byGuid.values()];
  }

  /**
   * HTTP client search mapped to omni results. Yields `[]` when the response is not OK or has no clients.
   * On transport/API errors: optional toast (except 404) and an Amplitude error event, then `[]`.
   */
  private _remoteSearchAsOmni(
    query: string
  ): Observable<OmniSearchResult<ClientSearchResultItem>[]> {
    return this._clientSearchService.searchClients(query).pipe(
      map((res) => {
        const items =
          res.result.code === GenericResponseCode.SERVICE_OK && res.payload
            ? (res.payload.clients ?? [])
            : [];

        if (!items.length) {
          return [];
        }

        return items.map((row) => ({
          title: this._titleForRow(row),
          description: this._descriptionForRow(row),
          data: row,
        }));
      }),
      catchError((err: unknown) => {
        const http = err as HttpErrorResponse;
        const msg =
          http?.error?.result?.message ??
          http?.error?.message ??
          http?.message ??
          'Search failed';
        if (http?.status && http.status !== 404) {
          this._toast.add({
            severity: 'error',
            summary: 'Client search',
            detail: msg,
            life: 8000,
          });
        }
        this._amplitude.logEvent('client_search_remote_error', {
          ...clientSearchAmplitudeProps(query),
          message: typeof msg === 'string' ? msg : 'unknown',
          status: http?.status ?? 0,
        });
        return of([]);
      })
    );
  }

  /** Lazy-loads the row component used to render each client omni result. */
  loadComponent = async () => ClientPrototypeSearchResultComponent;

  /**
   * Builds a {@link ClientSearchResultItem} from session subject data so local rows match the remote row shape
   * and share the same omni result component.
   */
  private _sessionToSearchItem(s: UiSession): ClientSearchResultItem {
    const d = s.subjectData;
    return {
      guid: d.guid,
      firstName: d.firstName,
      lastName: d.lastName,
      personCreateTs: null,
      personModifyTs: null,
      last4Ssn: null,
      dateOfBirth: d.dateOfBirth ?? null,
      mailingStreet: null,
      mailingCity: d.city ?? null,
      mailingState: d.state ?? null,
      preferredPhone: null,
      employeeId: null,
      customer: d.company ?? null,
    };
  }

  /** Primary label for a remote API hit (full name, or a generic fallback). */
  private _titleForRow(row: ClientSearchResultItem): string {
    const name = [row.firstName, row.lastName].filter(Boolean).join(' ');
    return name || 'Client';
  }

  /** Secondary line for a remote API hit: GUID and customer when available. */
  private _descriptionForRow(row: ClientSearchResultItem): string {
    const bits: string[] = [];
    if (row.guid) {
      bits.push(row.guid);
    }
    if (row.customer) {
      bits.push(row.customer);
    }
    return bits.join(' · ');
  }
}
